import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { LoanStep, TermUnit } from 'even-ts-static'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { coAppBadgeBreakpoint, PublicClassNames } from 'embed/constants'
import { isDev } from 'embed/constants/config'

import {
  APR_POSTFIX,
  EMPTY,
  getMaxAmountDisplay,
  getPreviewAprDisplay,
  getPreviewTermUnits,
  LEGAL,
  LOAN_AMOUNT_PREFIX,
  OPEN_TERM_LABEL,
  OPEN_TERM_LENGTH,
  PREVIEW_APR_LABEL,
  PREVIEW_MONTHLY_PAYMENT_LABEL,
  PREVIEW_TERM_LABEL,
} from 'embed/utils/offer-display-utils'

import { getContrastingColor } from 'embed/utils'
import { LoanOffer } from 'embed/constants/models'

import { BBBRatingsOnLoanOffers, Experiment, Original, Variant, withExperiment } from 'embed/experiments'

import { CoApplicantOfferBadge } from '../../CoApplicantOfferBadge'
import { Flag } from './flag'

import { BBBRating } from './BBBRating'
const BBBRatingExperiment = withExperiment(BBBRatingsOnLoanOffers)(BBBRating)

const styles = require('embed/styles/results')
const previewStyles = require('embed/styles/preview-offers')
interface Props {
  aprSuffix?: JSX.Element | string
  bestOfferId?: string
  disclaimer: React.ReactNode
  hideCta: boolean
  monthlyPaymentSuffix?: React.ReactNode
  offer: LoanOffer
  onLoanOfferCtaClick: (event: React.MouseEvent<Element>) => void
  partnerLogo: React.ReactNode
  selectedStep: LoanStep
  setEl: (el: HTMLLIElement) => void
  shouldDisplayTransunionDisclaimer: boolean
  trackingPixel: React.ReactNode
  viewportWidth: number
}

const ListItemFullPreview: React.SFC<Props> = ({
  aprSuffix,
  bestOfferId,
  disclaimer,
  hideCta,
  monthlyPaymentSuffix,
  offer,
  offer: { productSubType },
  onLoanOfferCtaClick,
  partnerLogo,
  selectedStep,
  setEl,
  shouldDisplayTransunionDisclaimer,
  trackingPixel,
  viewportWidth,
}) => {
  const flag = (
    <div className={styles.offerFlagContainer}>
      <Flag
        bestOfferId={bestOfferId}
        offer={offer}
        shouldDisplayTransunionDisclaimer={shouldDisplayTransunionDisclaimer}
        viewportWidth={viewportWidth}
      />
    </div>
  )

  const shouldShowOfferAmount = selectedStep === LoanStep.Results && offer.maxAmount
  const showCoApplicantOfferBadge =
    offer.coApplicant && viewportWidth <= coAppBadgeBreakpoint && offer.uuid === bestOfferId
  const coApplicantOfferBadge = showCoApplicantOfferBadge && <CoApplicantOfferBadge />

  let loanOfferTermlabel = PREVIEW_TERM_LABEL
  let loanOfferTermLength: JSX.Element | string = (
    <>
      {offer.termLength} <sup>{getPreviewTermUnits(offer)}</sup>
    </>
  )

  if (offer.termUnit === TermUnit.Open) {
    loanOfferTermlabel = OPEN_TERM_LABEL
    loanOfferTermLength = OPEN_TERM_LENGTH
  }

  return (
    <ThemeConsumer>
      {({
        borderRadiusLarge,
        borderRadiusSmall,
        colorBorder,
        ctaStyles,
        colorTextPrimary,
        colorTextSecondary,
        fontFamilyPrimary,
      }) => (
        <li
          data-test-val={isDev ? offer.uuid : ''}
          className={classnames(
            PublicClassNames.Offer,
            styles.offer,
            css`
              border-color: ${colorBorder};
              border-radius: ${borderRadiusLarge};
            `,
          )}
          ref={setEl}
        >
          {trackingPixel}
          {flag}
          {coApplicantOfferBadge}
          <div className={classnames(styles.offerWrapper, styles.loanOfferWrapper)}>
            <Experiment experiment={BBBRatingsOnLoanOffers}>
              <Original>
                <div className={classnames(styles.column, styles.originator)}>{partnerLogo}</div>
              </Original>
              <Variant experiment={BBBRatingsOnLoanOffers} variant={BBBRatingsOnLoanOffers.variants.BBBRatings}>
                <div className={classnames(styles.column, styles.originator, css(`text-align: center`))}>
                  {partnerLogo}
                  <BBBRatingExperiment rating={offer.originator?.bbbRating} />
                </div>
              </Variant>
            </Experiment>
            <table>
              <tbody>
                <tr>
                  <td className={classnames(previewStyles.previewLabel)}>
                    <p className={classnames(css(`color: ${colorTextSecondary}`))}>
                      {PREVIEW_APR_LABEL}
                      {aprSuffix}
                    </p>
                  </td>
                  <td className={classnames(previewStyles.previewLabel)}>
                    <p className={classnames(css(`color: ${colorTextSecondary}`))}>
                      {loanOfferTermlabel}
                      {offer.originator && offer.originator.key === 'light-stream' ? LEGAL : EMPTY}
                    </p>
                  </td>
                  <td className={classnames(previewStyles.previewLabel)}>
                    {offer.maxAmount && (
                      <p className={classnames(styles.monthlyPaymentLabel, css(`color: ${colorTextSecondary}`))}>
                        {PREVIEW_MONTHLY_PAYMENT_LABEL}
                        {monthlyPaymentSuffix}
                      </p>
                    )}
                  </td>
                </tr>
                <tr>
                  <td
                    data-test-id="offer_apr"
                    className={classnames(
                      styles.stat,
                      previewStyles.previewStat,
                      styles.previewAprStat,
                      css`
                        font-family: ${fontFamilyPrimary};
                      `,
                    )}
                  >
                    {getPreviewAprDisplay(offer)}
                    {APR_POSTFIX}
                  </td>
                  <td
                    data-test-id="offer_term"
                    className={classnames(
                      styles.stat,
                      previewStyles.previewStat,
                      css`
                        font-family: ${fontFamilyPrimary};
                      `,
                    )}
                  >
                    {loanOfferTermLength}
                  </td>
                  {offer.maxAmount && (
                    <td
                      data-test-id="offer_payment"
                      className={classnames(
                        styles.stat,
                        previewStyles.previewStat,
                        css`
                          font-family: ${fontFamilyPrimary};
                        `,
                      )}
                    >
                      {getMaxAmountDisplay(offer, LOAN_AMOUNT_PREFIX)}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
            {!hideCta && (
              <div className={classnames(previewStyles.cta, styles.cta)}>
                {shouldShowOfferAmount && (
                  <p data-test-id="offer_maxAmount" className={classnames(styles.stat, styles.offerAmount)}>
                    {LOAN_AMOUNT_PREFIX}
                    {getMaxAmountDisplay(offer)}
                  </p>
                )}
                <button
                  data-test-id="offer_continue"
                  className={classnames(
                    styles.button,
                    css(`
                    color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
                    border-radius: ${borderRadiusSmall};
                    &:hover {opacity: 0.84;}
                  `),
                  )}
                  style={ctaStyles}
                  onClick={onLoanOfferCtaClick}
                >
                  Get Matched
                </button>
              </div>
            )}
          </div>
          <div>{disclaimer}</div>
        </li>
      )}
    </ThemeConsumer>
  )
}

export default ListItemFullPreview
